import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'
import { of } from 'rxjs/observable/of'
import { _throw } from 'rxjs/observable/throw'
import { catchError, map } from 'rxjs/operators'

import { Deal, DealStatus, Meeting, Operator, PaginatedApi, Rate, SubscriptionPlan } from '../../models/models'
import { UserAuthProvider } from '../../modules/mrk-user-auth/user-auth'
import { SettingsProvider } from '../settings/settings'

@Injectable()
export class ApiProvider {

  MEETING_PLANNED = 1
  MEETING_DONE = 2
  MEETING_CANCEL = 3
  MEETING_LEAVED = 4

  MEETING_STATUS = []

  baseUrl: string;

  constructor(
    public http: HttpClient,
    private user: UserAuthProvider<any>,
    private readonly settings: SettingsProvider,
  ) {
    this.baseUrl = this.user.config.baseURL
    console.log('ApiProvider init');
    this.MEETING_STATUS[this.MEETING_PLANNED] = "Pianificato"
    this.MEETING_STATUS[this.MEETING_DONE] = "Fatto"
    this.MEETING_STATUS[this.MEETING_CANCEL] = "Annullato"
    this.MEETING_STATUS[this.MEETING_LEAVED] = "Non Presentato"
  }

  getPaginatedMeetings(operatorId: number, status: number[], is_finalized: boolean = null, deal__status: any = null, desc: boolean = false, page: number = 1, page_size: number = 30): Observable<PaginatedApi<Meeting>> {
    let params = new HttpParams();
    params = params.append('operator', "" + operatorId)
    params = params.append('page_size', "" + page_size)
    params = params.append('page', "" + page)
    if (status) {
      params = params.append('statuses', "" + status.join(','))
    }
    if (is_finalized != null) {
      params = params.append('is_finalized', is_finalized ? 'True' : 'False')
    }
    if (desc) {
      params = params.append('ordering', '-schedule_datetime')
    }

    // if (product) {
    //   params = params.append('deal__product__ac_label', product)
    // }
    if (deal__status) {
      params = params.append('deal__status', deal__status)
    }

    return this.http.get(this.baseUrl + "api/crm/meetings/", { params: params })
      .map((page: PaginatedApi<Meeting>) => {
        page.results = page.results.filter(m => this.settings.PRODUCTS.some( product => product == m.deal.product.ac_label ))
        return page
      }) as Observable<PaginatedApi<Meeting>>
  }

  getPlannedMeetingsFor(operatorId: number, deal: number): Observable<PaginatedApi<Meeting>> {
    let params = new HttpParams();
    params = params.append('operator', "" + operatorId)

    params = params.append('deal', `${deal}`)

    params = params.append('deal__status', `${this.MEETING_PLANNED}`)

    return this.http.get(this.baseUrl + "api/crm/meetings/", { params: params })
      .map((page: PaginatedApi<Meeting>) => {
        page.results = page.results.filter(m => this.settings.PRODUCTS.some(p => p == m.deal.product.ac_label))
        return page
      }) as Observable<PaginatedApi<Meeting>>
  }

  postDeal(contact_id: number, product_id: number, pipeline: number, stage: number, operator: number) {
    let payload = {
      contact: contact_id,
      product: product_id,
      current_pipeline: pipeline,
      current_stage: stage,
      current_operator: operator
    }
    return this.http.post(this.baseUrl + "api/crm/deals/", payload) as Observable<Deal>
    // .concatMap(
    //   updatedDeal => {
    //     return this.updateOnAC(updatedDeal).pipe(
    //       catchError((errorResponse: HttpErrorResponse) => {
    //         console.log(errorResponse)
    //         if (errorResponse.status === 409) {
    //           return of(updatedDeal).take(1)
    //         }
    //         return _throw(errorResponse)
    //       }),
    //       map(_ => updatedDeal))
    //})

  }


  getOperator(userId: number) {
    let params = new HttpParams();
    params = params.append('user', "" + userId)
    return this.http.get(this.baseUrl + "api/crm/operators/", { params: params })
      .map(data => data instanceof Array ? data[0] || null : data)
      .catch(_e => { return Observable.of(null) }) as Observable<Operator>
  }

  getOperators() {
    return this.http.get(this.baseUrl + "api/crm/operators/") as Observable<Operator[]>
  }

  postMeeting(operatorId: number, meeting: Meeting): Observable<Meeting> {
    let params = new HttpParams();
    params = params.append('operator', "" + operatorId)
    let payload = {
      deal: meeting.deal.id,
      schedule_datetime: meeting.schedule_datetime,
      notes: meeting.notes,
      place: meeting.place,
      schedule_by: operatorId
    }
    return this.http.post(this.baseUrl + "api/crm/meetings/", payload, { params: params })
  }

  patchMeeting(operatorId: number, meeting: Meeting): Observable<Meeting> {
    let params = new HttpParams();
    params = params.append('operator', "" + operatorId)
    let payload = {
      status: meeting.status
    }
    return this.http.patch(this.baseUrl + "api/crm/meetings/" + meeting.id + "/", payload, { params: params })
  }

  patchDeal(deal: Deal): Observable<Deal> {
    if (deal.status === DealStatus.WON) {
      return Observable.throw(new Error('Deal gia aperto'))
    }
    let payload = {
      status: deal.status,
      current_pipeline: deal.current_pipeline,
      current_stage: deal.current_stage,
    }
    return this.http.patch(this.baseUrl + "api/crm/deals/" + deal.id + "/", payload)
      .concatMap(
        updatedDeal => {
          return this.updateOnAC(updatedDeal).pipe(
            catchError((errorResponse: HttpErrorResponse) => {
              console.log(errorResponse)
              if (errorResponse.status === 409) {
                return of(updatedDeal).take(1)
              }
              return _throw(errorResponse)
            }),
            map(_ => updatedDeal))
        })
  }

  updateOnAC(deal: Deal): Observable<any> {
    return this.http.post(this.baseUrl + "api/crm/deals/" + deal.id + "/update_ac/", {})
  }

  markDealAsHot(deal: number, isHot: boolean) {
    let param = { is_hot: `${isHot}` }
    return this.http.patch<Deal>(this.baseUrl + "api/crm/deals/" + deal + "/", param)
  }

  setDealAsLost(deal: number) {
    let param = {
      is_hot: false,
      status: DealStatus.LOST
    }
    return this.http.patch<Deal>(this.baseUrl + "api/crm/deals/" + deal + "/", param)
  }

  getSubscriptionPlansForProduct(productLabel: String) {
    return this.http.get(`${this.baseUrl}api/subscription-plan/?product=${productLabel}`) as Observable<SubscriptionPlan[]>
  }

  getRates(subscriptionPlan: number) {
    return this.http.get(this.baseUrl + `api/subscription-plan/${subscriptionPlan}/rates/`) as Observable<Rate[]>
  }
}



// WEBPACK FOOTER //
// ./src/providers/api/api.ts